import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { marketplaceService } from '../services/marketplaceService';

export function useClubs(params?: {
  activity?: string;
  city?: string;
  level?: string;
  open_to_visitors?: boolean;
  q?: string;
}) {
  return useQuery({
    queryKey: ['marketplace', 'clubs', params],
    queryFn: () => marketplaceService.listClubs(params),
  });
}

export function useClub(slug: string) {
  return useQuery({
    queryKey: ['marketplace', 'club', slug],
    queryFn: () => marketplaceService.getClub(slug),
    enabled: !!slug,
  });
}

export function useClubSessions(slug: string) {
  return useQuery({
    queryKey: ['marketplace', 'club-sessions', slug],
    queryFn: () => marketplaceService.getClubSessions(slug),
    enabled: !!slug,
  });
}

export function useClubMembers(slug: string) {
  return useQuery({
    queryKey: ['marketplace', 'club-members', slug],
    queryFn: () => marketplaceService.getClubMembers(slug),
    enabled: !!slug,
  });
}

export function useClubsLeaderboard(params?: {
  city?: string;
  activity?: string;
  period?: 'week' | 'month' | 'all';
}) {
  return useQuery({
    queryKey: ['marketplace', 'clubs-leaderboard', params],
    queryFn: () => marketplaceService.getClubsLeaderboard(params),
  });
}

export function useRsvpSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clubSlug, sessionId }: { clubSlug: string; sessionId: string }) =>
      marketplaceService.rsvpSession(clubSlug, sessionId),
    onSuccess: (_data, { clubSlug }) => {
      qc.invalidateQueries({ queryKey: ['marketplace', 'club-sessions', clubSlug] });
    },
  });
}

export function useCancelRsvp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clubSlug, sessionId }: { clubSlug: string; sessionId: string }) =>
      marketplaceService.cancelRsvp(clubSlug, sessionId),
    onSuccess: (_data, { clubSlug }) => {
      qc.invalidateQueries({ queryKey: ['marketplace', 'club-sessions', clubSlug] });
    },
  });
}

export function useCreateClub() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: marketplaceService.createClub,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketplace', 'clubs'] });
    },
  });
}
